import { 
  CContainer, CRow, CCol, CCard, CCardBody, 
  CFormInput, CFormSelect, CButton, CFormLabel 
} from '@coreui/react';
import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { qrTagBatchSchema } from './schemas/createQrTagBatch.schema';
import { AppContext } from '../../context/AppContext';
import { CustomBreadcrumbs, LoadingOverlay } from '../../components';
import styles from './EditQrTagBatch.module.css';

const EditQrTagBatch = () => {
  const { qrTagBatchId } = useParams();
  const navigate = useNavigate();
  
  const { 
    updateQrTagBatch,
    getQrTagBatch,
    getCompaniesForSelect, 
    getBadgeSizes, 
    getCompanyTaxStatuses,
    getQrTagBatchStatuses 
  } = useContext(AppContext);
  
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Datos para selects
  const [companies, setCompanies] = useState([]);
  const [badgeSizes, setBadgeSizes] = useState([]);
  const [taxStatuses, setTaxStatuses] = useState([]);
  const [batchStatuses, setBatchStatuses] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [batchData, setBatchData] = useState(null);

  // Cargar opciones para selects + datos del batch
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoadingOptions(true);
        
        // Ejecutar todas las peticiones en paralelo
        const [companiesRes, badgeSizesRes, taxStatusesRes, statusesRes, batchRes] = await Promise.all([
          getCompaniesForSelect({ per_page: 1000 }),
          getBadgeSizes(),
          getCompanyTaxStatuses(),
          getQrTagBatchStatuses(),
          getQrTagBatch(qrTagBatchId),
        ]);
        
        // Actualizar estados con los resultados
        setCompanies(companiesRes.data || companiesRes || []);
        setBadgeSizes(badgeSizesRes.data || badgeSizesRes || []);
        setTaxStatuses(taxStatusesRes.data || taxStatusesRes || []);
        setBatchStatuses(statusesRes.data || statusesRes || []);
        setBatchData(batchRes.data || batchRes);
        
      } catch (error) {
        console.error('Error loading data:', error);
        alert('Error al cargar los datos del lote. Por favor, intentá de nuevo.');
      } finally {
        setLoadingOptions(false);
      }
    };
    
    loadAllData();
  }, [qrTagBatchId, getCompaniesForSelect, getBadgeSizes, getCompanyTaxStatuses, getQrTagBatchStatuses, getQrTagBatch]);

  // Valores iniciales del formulario (se completan cuando carga batchData)
  const initialValues = {
    qr_tags_amount: batchData?.qr_tags_amount || '',
    badge_size_id: batchData?.badge_size_id || '',
    company_id: batchData?.company?.id || '',
    company_name: batchData?.company_name || '',
    owner_name: batchData?.owner_name || '',
    email: batchData?.email || '',
    billing_name: batchData?.billing_name || '',
    billing_cuit: batchData?.billing_cuit || '',
    billing_email: batchData?.billing_email || '',
    billing_phone_country_code: batchData?.billing_phone_country_code || '',
    billing_phone_area_code: batchData?.billing_phone_area_code || '',
    billing_phone_number: batchData?.billing_phone_number || '',
    company_tax_status_id: batchData?.company_tax_status_id || '',
    qr_tag_batch_status_id: batchData?.qr_tag_batch_status_id || '',
    contact_name: batchData?.contact_name || '',
    contact_email: batchData?.contact_email || '',
    contact_phone_country_code: batchData?.contact_phone_country_code || '',
    contact_phone_area_code: batchData?.contact_phone_area_code || '',
    contact_phone_number: batchData?.contact_phone_number || '',
  };

  // Manejar submit
  const handleSubmit = async (values, { setErrors }) => {
    setLoading(true);
    
    try {
      // Filtrar valores vacíos
      const dataToSend = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== '' && value !== null)
      );
      
      await updateQrTagBatch(qrTagBatchId, dataToSend);
      
      // Mostrar éxito (SIN redirigir)
      setShowSuccess(true);
      
      // Ocultar toast después de 3 segundos
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error updating batch:', error);
      
      // Manejar errores de validación del backend
      if (error.response?.status === 422) {
        const errors = error.response.data.errors || {};
        setErrors(errors);
      } else {
        alert('Error al actualizar el lote. Por favor, intentá de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handler para cuando se selecciona una empresa
  const handleCompanyChange = (companyId, setValues, companies) => {
    if (!companyId) {
      // Si no hay empresa seleccionada, limpiar campos relacionados
      setValues(prev => ({
        ...prev,
        company_id: '',
        company_name: '',
        owner_name: '',
        email: '',
        billing_name: '',
        billing_cuit: '',
        billing_email: '',
        billing_phone_country_code: '',
        billing_phone_area_code: '',
        billing_phone_number: '',
        company_tax_status_id: '',
      }));
      return;
    }

    // Buscar la empresa seleccionada en el array
    const selectedCompany = companies.find(c => c.id === parseInt(companyId));
    
    if (selectedCompany) {
      // Precargar campos con los datos de la empresa
      setValues(prev => ({
        ...prev,
        company_id: selectedCompany.id,
        company_name: selectedCompany.company_name || '',
        owner_name: selectedCompany.owner_name || '',
        email: selectedCompany.email || '',
        billing_name: selectedCompany.billing_name || '',
        billing_cuit: selectedCompany.billing_cuit || '',
        billing_email: selectedCompany.billing_email || '',
        billing_phone_country_code: selectedCompany.billing_phone_country_code || '',
        billing_phone_area_code: selectedCompany.billing_phone_area_code || '',
        billing_phone_number: selectedCompany.billing_phone_number || '',
        company_tax_status_id: selectedCompany.company_tax_status?.id || '',
      }));
    }
  };

  // Si aún está cargando, mostrar loading
  if (loadingOptions || !batchData) {
    return (
      <div>
        <LoadingOverlay isVisible={true} message="Cargando datos del lote..." />
      </div>
    );
  }

  return (
    <div>
      {/* Overlay de carga */}
      <LoadingOverlay 
        isVisible={loading || loadingOptions}
        message={loading ? "Guardando cambios..." : "Cargando opciones..."}
      />
      
      {/* Toast de éxito */}
      {showSuccess && (
        <div className={styles.successToast}>
          ✅ Lote actualizado exitosamente
        </div>
      )}
      
      <CustomBreadcrumbs 
        parentTitle={'QR Tags'} 
        activeTitle={`Editar Lote #${qrTagBatchId}`} 
      />
      
      <CContainer className="mt-4">
        <CRow>
          <CCol xs={12}>
            <h2 className="title mb-4">Editar Lote de QR Tags</h2>
          </CCol>
        </CRow>

        <Formik
          initialValues={initialValues}
          validationSchema={qrTagBatchSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true} // ✅ Importante: reinicializar cuando batchData carga
        >
          {({ values, handleChange, handleBlur, setValues, isSubmitting }) => (
            <Form>
              {/* Sección 1: Datos del Lote */}
              <CCard className="mb-4">
                <CCardBody>
                  <h4 className="mb-3">Datos del Lote</h4>
                  <CRow>
                    <CCol xs={12} lg={4}>
                      <CFormLabel htmlFor="qr_tags_amount">
                        Cantidad de QRs *
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="number"
                        id="qr_tags_amount"
                        name="qr_tags_amount"
                        placeholder="Ej: 50"
                        min="1"
                        max="1000"
                      />
                      <ErrorMessage 
                        name="qr_tags_amount" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={4}>
                      <CFormLabel htmlFor="badge_size_id">
                        Tamaño de Badge *
                      </CFormLabel>
                      <Field
                        as={CFormSelect}
                        id="badge_size_id"
                        name="badge_size_id"
                      >
                        <option value="">Seleccionar...</option>
                        {badgeSizes.map(size => (
                          <option key={size.id} value={size.id}>
                            {size.name} ({size.measures})
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage 
                        name="badge_size_id" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>

                    <CCol xs={12} lg={4}>
                        <CFormLabel htmlFor="qr_tag_batch_status_id">
                            Estado *
                        </CFormLabel>
                        <Field
                            as={CFormSelect}
                            id="qr_tag_batch_status_id"
                            name="qr_tag_batch_status_id"
                        >
                            <option value="">Seleccionar...</option>
                            {batchStatuses.map(status => (
                                <option key={status.id} value={status.id}>
                                    {status.name}
                                </option>
                            ))}
                        </Field>
                        <ErrorMessage 
                            name="qr_tag_batch_status_id" 
                            component="div" 
                            className={styles.error}
                        />
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>

              {/* Sección 2: Empresa */}
              <CCard className="mb-4">
                <CCardBody>
                  <h4 className="mb-3">Empresa</h4>
                  <CRow>
                    <CCol xs={12} lg={6}>
                      <CFormLabel htmlFor="company_id">
                        Empresa (opcional)
                      </CFormLabel>
                      <Field
                        as={CFormSelect}
                        id="company_id"
                        name="company_id"
                        onChange={(e) => {
                          const companyId = e.target.value;
                          handleCompanyChange(companyId, setValues, companies);
                          handleChange(e); // Mantener el cambio de Formik
                        }}
                      >
                        <option value="">Seleccionar...</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.company_name}
                          </option>
                        ))}
                      </Field>
                    </CCol>
                    
                    <CCol xs={12} lg={6}>
                      <CFormLabel htmlFor="company_name">
                        Nombre de Empresa (texto libre)
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="company_name"
                        name="company_name"
                        placeholder="Ej: Mi Empresa S.A."
                      />
                      <ErrorMessage 
                        name="company_name" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="owner_name">
                        Nombre del Propietario
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="owner_name"
                        name="owner_name"
                        placeholder="Ej: Juan Pérez"
                      />
                      <ErrorMessage 
                        name="owner_name" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="email">
                        Email del Propietario
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Ej: juan@empresa.com"
                      />
                      <ErrorMessage 
                        name="email" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="billing_name">
                        Nombre/Razón Social
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="billing_name"
                        name="billing_name"
                        placeholder="Ej: Mi Empresa S.A."
                      />
                      <ErrorMessage 
                        name="billing_name" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="billing_cuit">
                        CUIT/CUIL
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="billing_cuit"
                        name="billing_cuit"
                        placeholder="Ej: 30-12345678-9"
                      />
                      <ErrorMessage 
                        name="billing_cuit" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="billing_email">
                        Email de Facturación
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="email"
                        id="billing_email"
                        name="billing_email"
                        placeholder="Ej: facturacion@empresa.com"
                      />
                      <ErrorMessage 
                        name="billing_email" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={6} className="mt-3">
                      <CFormLabel htmlFor="company_tax_status_id">
                        Condición IVA
                      </CFormLabel>
                      <Field
                        as={CFormSelect}
                        id="company_tax_status_id"
                        name="company_tax_status_id"
                      >
                        <option value="">Seleccionar...</option>
                        {taxStatuses.map(status => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage 
                        name="company_tax_status_id" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>

                    <CCol xs={12} lg={4} className="mt-3">
                      <CFormLabel htmlFor="billing_phone_country_code">
                        Código de País
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="billing_phone_country_code"
                        name="billing_phone_country_code"
                        placeholder="Ej: +54"
                      />
                      <ErrorMessage 
                        name="billing_phone_country_code" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={4} className="mt-3">
                      <CFormLabel htmlFor="billing_phone_area_code">
                        Código de Área
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="billing_phone_area_code"
                        name="billing_phone_area_code"
                        placeholder="Ej: 11"
                      />
                      <ErrorMessage 
                        name="billing_phone_area_code" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={4} className="mt-3">
                      <CFormLabel htmlFor="billing_phone_number">
                        Número de Teléfono
                      </CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="billing_phone_number"
                        name="billing_phone_number"
                        placeholder="Ej: 4567-8901"
                      />
                      <ErrorMessage 
                        name="billing_phone_number" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>

              {/* Sección 3: Persona de Contacto */}
              <CCard className="mb-4">
                <CCardBody>
                  <h4 className="mb-3">Persona de Contacto</h4>
                  
                  {/* Nombre y Email */}
                  <CRow>
                    <CCol xs={12} lg={6}>
                      <CFormLabel htmlFor="contact_name">Nombre del Contacto</CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="contact_name"
                        name="contact_name"
                        placeholder="Ej: María González"
                      />
                      <ErrorMessage 
                        name="contact_name" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={6}>
                      <CFormLabel htmlFor="contact_email">Email del Contacto</CFormLabel>
                      <Field
                        as={CFormInput}
                        type="email"
                        id="contact_email"
                        name="contact_email"
                        placeholder="Ej: maria@empresa.com"
                      />
                      <ErrorMessage 
                        name="contact_email" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                  </CRow>
                  
                  {/* Teléfono (3 partes) */}
                  <CRow className="mt-3">
                    <CCol xs={12} lg={4}>
                      <CFormLabel htmlFor="contact_phone_country_code">Código de País</CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="contact_phone_country_code"
                        name="contact_phone_country_code"
                        placeholder="+54"
                      />
                      <ErrorMessage 
                        name="contact_phone_country_code" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={4}>
                      <CFormLabel htmlFor="contact_phone_area_code">Código de Área</CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="contact_phone_area_code"
                        name="contact_phone_area_code"
                        placeholder="11"
                      />
                      <ErrorMessage 
                        name="contact_phone_area_code" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                    
                    <CCol xs={12} lg={4}>
                      <CFormLabel htmlFor="contact_phone_number">Número</CFormLabel>
                      <Field
                        as={CFormInput}
                        type="text"
                        id="contact_phone_number"
                        name="contact_phone_number"
                        placeholder="4567-8901"
                      />
                      <ErrorMessage 
                        name="contact_phone_number" 
                        component="div" 
                        className={styles.error}
                      />
                    </CCol>
                  </CRow>
                </CCardBody>
              </CCard>

              {/* Botones de acción */}
              <CRow className="mb-5">
                <CCol xs={12}>
                  <div className="d-flex gap-3">
                    <CButton 
                      type="submit" 
                      color="primary"
                      disabled={isSubmitting || loadingOptions}
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                    </CButton>
                    <CButton 
                      type="button"
                      color="secondary"
                      onClick={() => navigate('/dashboard/qr-tag-batches')}
                    >
                      Cancelar
                    </CButton>
                  </div>
                </CCol>
              </CRow>
            </Form>
          )}
        </Formik>
      </CContainer>
    </div>
  );
};

export default EditQrTagBatch;